<template>
  <div
    class="scrollControlBar__broadcast"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <transition name="broadcast-fade" mode="out-in">
      <div
        :key="currentSlide.id"
        class="scrollControlBar__broadcastFrame"
      >
        <!-- scope 默认态：与原版「全部资源」视觉一致 -->
        <span
          v-if="currentSlide.type === 'scope-default'"
          class="scrollControlBar__scopeEmpty"
        >
          {{ currentSlide.label || '全部资源' }}
        </span>

        <!-- 版本升级说明：单行摘要，可点击看详情 -->
        <el-popover
          v-else-if="currentSlide.type === 'release-note' && hasDetail"
          placement="bottom"
          trigger="click"
          popper-class="linkBroadcastPopover"
          :title="currentSlide.title"
        >
          <ul class="linkBroadcastPopover__list">
            <li
              v-for="(line, index) in currentSlide.detailLines"
              :key="index"
            >
              {{ line }}
            </li>
          </ul>
          <button
            slot="reference"
            type="button"
            class="scrollControlBar__broadcastNote scrollControlBar__broadcastNote--clickable"
          >
            <span class="scrollControlBar__broadcastNoteTitle">{{ currentSlide.title }}</span>
            <span class="scrollControlBar__broadcastNoteSep" aria-hidden="true">·</span>
            <span class="scrollControlBar__broadcastNoteSummary">{{ currentSlide.summary }}</span>
          </button>
        </el-popover>

        <div
          v-else-if="currentSlide.type === 'release-note'"
          class="scrollControlBar__broadcastNote"
        >
          <span class="scrollControlBar__broadcastNoteTitle">{{ currentSlide.title }}</span>
          <span class="scrollControlBar__broadcastNoteSep" aria-hidden="true">·</span>
          <span class="scrollControlBar__broadcastNoteSummary">{{ currentSlide.summary }}</span>
        </div>
      </div>
    </transition>

    <span
      v-if="slides.length > 1"
      class="scrollControlBar__broadcastIndex"
      aria-hidden="true"
    >
      {{ currentIndex + 1 }}/{{ slides.length }}
    </span>
  </div>
</template>

<script>
import { getSlideDwellMs } from './controlBarBroadcast/broadcastHelper'
import { CONTROL_BAR_BROADCAST } from './controlBarBroadcast/broadcastConfig'

/**
 * 控制条 idle 态轮播：按 broadcastConfig 顺序切换 scope 默认帧与版本说明帧。
 * 每帧停留时长由 slide.dwellMs 决定；hover 时暂停，便于运营读完当前内容。
 */
export default {
  name: 'ControlBarCarousel',
  props: {
    slides: {
      type: Array,
      required: true,
    },
    pauseOnHover: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      timerId: null,
      hoverPaused: false,
    }
  },
  computed: {
    currentSlide() {
      if (!this.slides.length) {
        return { id: 'fallback', type: 'scope-default', label: '全部资源' }
      }
      return this.slides[this.currentIndex] || this.slides[0]
    },
    hasDetail() {
      const lines = this.currentSlide.detailLines
      return Array.isArray(lines) && lines.length > 0
    },
  },
  watch: {
    slides: {
      handler() {
        this.resetCarousel()
      },
      deep: true,
    },
  },
  mounted() {
    this.scheduleNext()
  },
  beforeDestroy() {
    this.clearTimer()
  },
  methods: {
    onMouseEnter() {
      if (!this.pauseOnHover) {
        return
      }
      this.hoverPaused = true
      this.clearTimer()
    },
    onMouseLeave() {
      if (!this.pauseOnHover) {
        return
      }
      this.hoverPaused = false
      this.scheduleNext()
    },
    resetCarousel() {
      this.currentIndex = 0
      this.clearTimer()
      if (!this.hoverPaused) {
        this.scheduleNext()
      }
    },
    advance() {
      if (this.slides.length <= 1) {
        return
      }
      this.currentIndex = (this.currentIndex + 1) % this.slides.length
      this.scheduleNext()
    },
    scheduleNext() {
      this.clearTimer()
      if (this.hoverPaused || this.slides.length <= 1) {
        return
      }
      const dwell = getSlideDwellMs(this.currentSlide, CONTROL_BAR_BROADCAST)
      this.timerId = window.setTimeout(() => {
        this.advance()
      }, dwell)
    },
    clearTimer() {
      if (this.timerId != null) {
        window.clearTimeout(this.timerId)
        this.timerId = null
      }
    },
  },
}
</script>

<style scoped>
.scrollControlBar__broadcast {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: var(--link-display-height, 36px);
}

.scrollControlBar__broadcastFrame {
  flex: 1 1 auto;
  min-width: 0;
}

.scrollControlBar__broadcastIndex {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.broadcast-fade-enter-active,
.broadcast-fade-leave-active {
  transition: opacity 0.18s ease;
}

.broadcast-fade-enter,
.broadcast-fade-leave-to {
  opacity: 0;
}
</style>
